'use client';

import Link from 'next/link';
import { Button } from '@/components/button';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/card';
import { getModeConfig } from '@/lib/modes';
import { Mail, CheckCircle2, FileText } from 'lucide-react';
import { useMemo } from 'react';

const SALES_URL = 'https://wisprs.co/sales';

// AEO / structured data — claims mirror the on-page copy + the shipped Sales Call Kit.
const salesSoftwareSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Wisprs — Sales Call Transcription',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', availability: 'https://schema.org/InStock', url: SALES_URL },
  url: SALES_URL,
  description:
    'Turn a sales call recording into a ready-to-send follow-up email, a next-steps checklist, and a formatted CRM note.',
};

const salesBreadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://wisprs.co' },
    { '@type': 'ListItem', position: 2, name: 'For Sales Teams', item: SALES_URL },
  ],
};

const salesHowToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to turn a sales call into a follow-up email and CRM note',
  step: [
    { '@type': 'HowToStep', name: 'Upload the call', text: 'Upload your sales call recording — no manual note-taking during the call.' },
    { '@type': 'HowToStep', name: 'Get the kit', text: 'Wisprs returns a follow-up email, a checklist of next steps, and a formatted CRM note.' },
    { '@type': 'HowToStep', name: 'Copy and send', text: 'Send the email and paste the note straight into your CRM.' },
  ],
};

const salesFaqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What does Wisprs produce from a sales call?',
      acceptedAnswer: { '@type': 'Answer', text: 'From one uploaded recording you get a ready-to-send follow-up email, a checklist of next steps, and a formatted CRM note.' },
    },
    {
      '@type': 'Question',
      name: 'Can I paste the note straight into my CRM?',
      acceptedAnswer: { '@type': 'Answer', text: 'Yes. The CRM note is formatted to paste into your CRM, and the follow-up email is ready to send.' },
    },
    {
      '@type': 'Question',
      name: 'How accurate is the transcription?',
      acceptedAnswer: { '@type': 'Answer', text: 'Excellent accuracy on clear audio, with speaker labels. Quality varies by language, accent, and recording conditions.' },
    },
    {
      '@type': 'Question',
      name: 'Is there a free way to try it?',
      acceptedAnswer: { '@type': 'Answer', text: 'Yes. Start free and upload one call — no credit card required.' },
    },
  ],
};

export default function SalesLandingPage() {
  const salesMode = useMemo(() => {
    try {
      const mode = getModeConfig('sales');

      if (!mode || !mode.landingPage) {
        console.error('Sales mode configuration is missing landingPage');

        return null;
      }

      return mode;
    } catch (error) {
      console.error('Error loading sales mode:', error);

      return null;
    }
  }, []);
  
  // Safety check for landingPage
  if (!salesMode?.landingPage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[rgb(var(--background))]">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[rgb(var(--foreground))] mb-4">
            Configuration Error
          </h1>
          <p className="text-[rgb(var(--foreground-secondary))]">
            Sales mode configuration is not available. Please try again later.
          </p>
        </div>
      </div>
    );
  }
  
  const { hero, features } = salesMode.landingPage;

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden w-full">
      <Navigation />
      
      <main className="flex-1 w-full overflow-x-hidden">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(salesSoftwareSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(salesBreadcrumbSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(salesHowToSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(salesFaqSchema) }} />
        {/* Hero Section */}
        <section className="relative py-24 md:py-32 border-b border-[rgb(var(--surface-border))] bg-[rgb(var(--background))] overflow-hidden">
          {/* Animated Waveform Background */}
          <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
            <svg
              className="absolute inset-0 w-full h-full"
              preserveAspectRatio="none"
              viewBox="0 0 1200 600"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <linearGradient id="salesBarGradient1" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#8B5CF6" />
                  <stop offset="50%" stopColor="#EC4899" />
                  <stop offset="100%" stopColor="#F59E0B" />
                </linearGradient>
              </defs>
              
              {Array.from({ length: 50 }).map((_, i) => {
                const x = (i * 24) + 12;
                const baseHeight = Math.round((60 + (Math.sin(i * 0.4) * 50) + (Math.cos(i * 0.2) * 30)) * 100) / 100;
                const y = Math.round((300 - baseHeight / 2) * 100) / 100;
                
                return (
                  <rect
                    key={i}
                    x={x - 2}
                    y={y}
                    width="4"
                    height={baseHeight}
                    rx="2"
                    fill="url(#salesBarGradient1)"
                    className={`waveform-bar-${i % 6}`}
                  />
                );
              })}
            </svg>
          </div>

          <div className="relative z-10 mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center space-y-8">
              <h1 className="text-4xl md:text-6xl font-bold text-[rgb(var(--foreground))] leading-tight">
                {hero.title}
              </h1>
              <p className="text-xl md:text-2xl text-[rgb(var(--foreground-secondary))] max-w-2xl mx-auto">
                {hero.description}
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                <Link href="/dashboard/upload">
                  <Button
                    variant="primary"
                    size="lg"
                    className="bg-gradient-to-r from-[#8B5CF6] via-[#EC4899] to-[#F59E0B] hover:from-[#7C3AED] hover:via-[#DB2777] hover:to-[#D97706] text-white border-0 w-full sm:w-auto"
                  >
                    {hero.cta.primary}
                  </Button>
                </Link>
                {hero.cta.secondary && (
                  <Link href="/dashboard/pricing">
                    <Button
                      variant="secondary"
                      size="lg"
                      className="w-full sm:w-auto"
                    >
                      {hero.cta.secondary}
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-24 md:py-32 border-b border-[rgb(var(--surface-border))] bg-[rgb(var(--background))]">
          <div className="mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[rgb(var(--foreground))] mb-4">
                What you get in 2 minutes
              </h2>
              <p className="text-lg text-[rgb(var(--foreground-secondary))] max-w-2xl mx-auto">
                Upload your sales call and get everything you need to follow up
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 min-w-0">
              {features.map((feature, index) => (
                <Card key={index} variant="elevated" className="p-6">
                  <CardHeader className="p-0 mb-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-[#8B5CF6] via-[#EC4899] to-[#F59E0B] flex items-center justify-center mb-4">
                      {index === 0 && <Mail className="w-6 h-6 text-white" />}
                      {index === 1 && <CheckCircle2 className="w-6 h-6 text-white" />}
                      {index === 2 && <FileText className="w-6 h-6 text-white" />}
                    </div>
                    <CardTitle>{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <CardDescription>{feature.description}</CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-24 md:py-32 border-b border-[rgb(var(--surface-border))] bg-[rgb(var(--background))]">
          <div className="mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[rgb(var(--foreground))] mb-4">
                How it works
              </h2>
            </div>

            <div className="max-w-3xl mx-auto space-y-8">
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-r from-[#8B5CF6] via-[#EC4899] to-[#F59E0B] flex items-center justify-center text-white font-bold text-lg">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[rgb(var(--foreground))] mb-2">
                    Upload your call recording
                  </h3>
                  <p className="text-[rgb(var(--foreground-secondary))]">
                    Drag and drop or upload your sales call audio or video file. We support all major formats.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-r from-[#8B5CF6] via-[#EC4899] to-[#F59E0B] flex items-center justify-center text-white font-bold text-lg">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[rgb(var(--foreground))] mb-2">
                    Get your Sales Call Kit
                  </h3>
                  <p className="text-[rgb(var(--foreground-secondary))]">
                    In about 2 minutes, you&apos;ll have a follow-up email, next steps checklist, and CRM note ready to copy.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-r from-[#8B5CF6] via-[#EC4899] to-[#F59E0B] flex items-center justify-center text-white font-bold text-lg">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[rgb(var(--foreground))] mb-2">
                    Copy and send
                  </h3>
                  <p className="text-[rgb(var(--foreground-secondary))]">
                    One click to copy your follow-up email, then paste into your CRM. Done in seconds, not hours.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 md:py-32 bg-[rgb(var(--background))]">
          <div className="mx-auto max-w-4xl w-full px-4 sm:px-6 lg:px-8">
            <div className="text-center space-y-8">
              <h2 className="text-3xl md:text-4xl font-bold text-[rgb(var(--foreground))]">
                Ready to save 30+ minutes per call?
              </h2>
              <p className="text-lg text-[rgb(var(--foreground-secondary))]">
                Start with one call. See the difference immediately.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link href="/dashboard/upload">
                  <Button
                    variant="primary"
                    size="lg"
                    className="bg-gradient-to-r from-[#8B5CF6] via-[#EC4899] to-[#F59E0B] hover:from-[#7C3AED] hover:via-[#DB2777] hover:to-[#D97706] text-white border-0 w-full sm:w-auto"
                  >
                    Upload one call
                  </Button>
                </Link>
                <Link href="/sales/use-case">
                  <Button
                    variant="secondary"
                    size="lg"
                    className="w-full sm:w-auto"
                  >
                    Learn more
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}

